import { useState, useMemo } from "react";
import styles from "./ChartOfAccounts.module.css";

import {
  Add,
  Edit,
  Delete,
  Block,
  CheckCircle,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";

import { type IPlanoContas } from "../../../../services/financial";
import TableContainer from "../../../../components/TableContainer/TableContainer";

interface PlanOfAccountsListProps {
  rows: IPlanoContas[];
  handleEdit: (item: IPlanoContas) => void;
  handleToggleStatus?: (item: IPlanoContas) => void;
  handleDelete?: (uid: string) => void;
  handleAddChild: (item: IPlanoContas) => void;
}

const PlanOfAccountsList = ({ rows, handleEdit, handleToggleStatus, handleDelete, handleAddChild }: PlanOfAccountsListProps) => {

  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const activeStatusMap = useMemo(() => {
    const map = new Map<string, boolean>();
    rows.forEach(row => {
      map.set(row.uid_plano_contas, row.ativo);
    });
    return map;
  }, [rows]);

  const parentIdsWithChildren = useMemo(() => {
    const parents = new Set<string>();
    rows.forEach(row => {
      if (row.uid_plano_contas_pai) {
        parents.add(row.uid_plano_contas_pai);
      }
    });
    return parents;
  }, [rows]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev =>
      prev.includes(id)
        ? prev.filter(expandedId => expandedId !== id)
        : [...prev, id]
    );
  };

  const getLevel = (codigo: string) => {
    if (!codigo) return 0;
    return codigo.split('.').length - 1;
  };

  const isRowVisible = (item: IPlanoContas) => {
    if (!item.uid_plano_contas_pai) return true;
    return expandedIds.includes(item.uid_plano_contas_pai);
  };

  return (
    <div className={styles.containerTable}>
      <TableContainer
        columns={[
          { key: "codigoEx", titulo: "Cód", classStyle: styles.columnCode },
          { key: "descricaoEx", titulo: "Descrição", classStyle: styles.columnName },
          { key: "tipoEx", titulo: "Tipo", classStyle: styles.columnType },
          { key: "statusEx", titulo: "Status", classStyle: styles.columnStatus },
          { key: "actionEx", titulo: "Ação", classStyle: styles.columnAction },
        ]}
        content={
          rows
            ?.filter((item) => isRowVisible(item))
            ?.map((item) => {
              const level = getLevel(item.codigo_classificacao);
              const hasChildren = parentIdsWithChildren.has(item.uid_plano_contas);
              const isExpanded = expandedIds.includes(item.uid_plano_contas);
              const isRoot = !item.uid_plano_contas_pai;
              const isParentActive = item.uid_plano_contas_pai
                ? activeStatusMap.get(item.uid_plano_contas_pai)
                : true;

              const isToggleDisabled = !item.ativo && !isParentActive;
              const toggleTitle = isToggleDisabled
                ? "Não é possível ativar pois a conta pai está inativa"
                : item.ativo ? "Inativar" : "Ativar";

              return {
                rowClass: `${styles.tableItem} ${isRoot ? styles.rootRow : ""}`,
                values: {
                  codigoEx: (
                    <span className={styles.codeBadge}>{item.codigo_classificacao}</span>
                  ),
                  descricaoEx: (
                    <div
                      className={styles.treeNode}
                      style={{ marginLeft: `${level * 10}px` }}
                    >
                      <div
                        className={styles.toggleIconWrapper}
                        onClick={() => hasChildren && toggleExpand(item.uid_plano_contas)}
                        style={{
                          cursor: hasChildren ? "pointer" : "default",
                          visibility: hasChildren ? "visible" : "hidden",
                        }}
                      >
                        {isExpanded ? (
                          <KeyboardArrowDown fontSize="small" />
                        ) : (
                          <KeyboardArrowRight fontSize="small" />
                        )}
                      </div>
                      <span
                        className={styles.nodeName}
                        onClick={() => hasChildren && toggleExpand(item.uid_plano_contas)}
                        style={{
                          fontWeight: isRoot || hasChildren ? "600" : "400",
                          cursor: hasChildren ? "pointer" : "default",
                        }}
                      >
                        {item.nome}
                      </span>
                    </div>
                  ),
                  tipoEx: (
                    <span
                      className={`${styles.typeBadge} ${
                        item.tipo === "receita" ? styles.typeReceita : styles.typeDespesa
                      }`}
                    >
                      {item.tipo === "receita" ? "Receita" : "Despesa"}
                    </span>
                  ),
                  statusEx: (
                    <span
                      className={`${styles.statusBadge} ${
                        item.ativo ? styles["status-active"] : styles["status-inactive"]
                      }`}
                    >
                      {item.ativo ? "Ativo" : "Inativo"}
                    </span>
                  ),
                },
                buttons: [
                  {
                    variant: "contain",
                    typeStyle: "small",
                    title: "Criar subnível",
                    onClick: () => handleAddChild(item),
                    disabled: !item.ativo,
                    icon: <Add />,
                  },
                  {
                    variant: "contain",
                    typeStyle: "small",
                    title: "Editar",
                    onClick: () => handleEdit(item),
                    icon: <Edit />,
                  },
                  {
                    variant: "contain",
                    typeStyle: "small",
                    title: toggleTitle,
                    disabled: isToggleDisabled,
                    onClick: () => !isToggleDisabled && handleToggleStatus?.(item),
                    style: isToggleDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {},
                    icon: item.ativo ? (
                      <Block sx={{ fontSize: 20 }} />
                    ) : (
                      <CheckCircle />
                    ),
                  },
                  {
                    variant: "contain",
                    typeStyle: ["small", "cancel"],
                    title: item.tem_vinculos ? "Possui vínculos" : "Excluir",
                    disabled: item.tem_vinculos,
                    onClick: () => handleDelete?.(item.uid_plano_contas),
                    icon: <Delete />,
                  },
                ],
              };
            }) ?? []
        }
      />
    </div>
  );
};

export default PlanOfAccountsList;